import { Color, FrontSide, ShaderMaterial, Vector3 } from 'three';
import { supportThreshold } from '../../utils/support-area.util';

export const OVERHANG_COLOR = 0xe53935;

/** How far past the critical angle [°] the paint takes to reach full strength. */
export const OVERHANG_SOFTNESS_DEG = 2;

/** Below any n·d, so nothing is painted. */
const NO_SUPPORT = -2;

const vertexShader = /* glsl */ `
#include <common>
#include <clipping_planes_pars_vertex>

attribute vec4 supportNormal;

varying vec3 vViewPos;
varying vec4 vSupportNormal;

void main() {
  vSupportNormal = vec4(normalMatrix * supportNormal.xyz, supportNormal.w);
  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
  vViewPos = mvPosition.xyz;
  gl_Position = projectionMatrix * mvPosition;
  #include <clipping_planes_vertex>
}
`;

const fragmentShader = /* glsl */ `
#include <common>
#include <clipping_planes_pars_fragment>

uniform vec3 uColor;
uniform vec3 uDirection;
uniform float uThreshold;
uniform float uSoftness;

varying vec3 vViewPos;
varying vec4 vSupportNormal;

void main() {
  #include <clipping_planes_fragment>
  vec3 facet = normalize(cross(dFdx(vViewPos), dFdy(vViewPos)));
  // Crease corners (w = 0) take the facet's own normal.
  vec3 normal = vSupportNormal.xyz + (1.0 - vSupportNormal.w) * facet;
  if (dot(normal, normal) < 1e-8) discard;
  float lean = dot(normalize(normal), normalize((viewMatrix * vec4(uDirection, 0.0)).xyz));
  // 0 at the threshold, 1 from uSoftness (at least a pixel and a half) past it.
  float band = max(uSoftness, 1.5 * fwidth(lean));
  float strength = 1.0 - smoothstep(uThreshold - band, uThreshold, lean);
  if (strength <= 0.0) discard;
  // Shaded by how squarely the facet faces the camera, so the red keeps the part's flat facets.
  float facing = abs(dot(facet, normalize(-vViewPos)));
  gl_FragColor = vec4(uColor * (0.55 + 0.45 * facing), strength);
  #include <colorspace_fragment>
}
`;

/**
 * Paints, over the part it is drawn on, where the part needs support along a
 * build direction, fading in over OVERHANG_SOFTNESS_DEG past the critical
 * angle. The test runs per pixel on the interpolated `supportNormal` (see
 * supportVertexNormals, in the mesh's own frame), so the region follows the
 * surface smoothly instead of triangle by triangle. Aiming it only changes
 * uniforms.
 */
export function createOverhangMaterial(): ShaderMaterial {
  return new ShaderMaterial({
    name: 'OverhangMaterial',
    uniforms: {
      uColor: { value: new Color(OVERHANG_COLOR) },
      uDirection: { value: new Vector3(0, 0, 1) },
      uThreshold: { value: NO_SUPPORT },
      uSoftness: { value: overhangSoftness(45) },
    },
    vertexShader,
    fragmentShader,
    side: FrontSide,
    clipping: true,
    transparent: true,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
    depthWrite: false,
  });
}

/**
 * Aims the material at a build direction in world space. Paint starts at the
 * support-area rule's own threshold. A zero direction paints nothing.
 */
export function aimOverhangMaterial(material: ShaderMaterial, worldDirection: Vector3, critAngleDeg: number): void {
  const uniforms = material.uniforms;
  const length = worldDirection.length();
  if (!(length > 0)) {
    uniforms['uThreshold'].value = NO_SUPPORT;
    return;
  }
  (uniforms['uDirection'].value as Vector3).copy(worldDirection).divideScalar(length);
  uniforms['uThreshold'].value = supportThreshold(critAngleDeg);
  uniforms['uSoftness'].value = overhangSoftness(critAngleDeg);
}

/** The drop in n·d over OVERHANG_SOFTNESS_DEG past the critical angle, cut at straight down. */
function overhangSoftness(critAngleDeg: number): number {
  const rad = Math.PI / 180;
  const past = Math.min(critAngleDeg + OVERHANG_SOFTNESS_DEG, 90);
  return Math.max(Math.sin(past * rad) - Math.sin(critAngleDeg * rad), 1e-4);
}
